import type { Copy } from './en';
import { LOCALE_INFO } from './locale';

/**
 * Japanese copy. Terms follow the app's own app_ja.arb (旅, チェックイン, デイリーハイライト,
 * パスポート, 旅程, 発見, ホームタウン, 自分の旅に追加) so the site and app read the same.
 */

export const ja: Copy = {
  htmlLang: LOCALE_INFO.ja.htmlLang,
  phraseGap: '',
  ogLocale: 'ja_JP',
  languageName: '日本語',

  nav: {
    home: 'Travo ホーム',
    primary: 'メインメニュー',
    features: '機能',
    plan: '旅を計画',
    about: '私たちについて',
    download: 'アプリを入手',
    openMenu: 'メニューを開く',
    closeMenu: 'メニューを閉じる',
    language: '言語',
    byLabs: 'Travo Labs 制作'
  },

  download: {
    appStore: 'App Store からダウンロード',
    playStore: 'Google Play で手に入れよう',
    ios: 'iOS（App Store）',
    android: 'Android（Google Play）',
    free: '無料 · iOS & ANDROID'
  },

  seo: {
    ogImage: '/og-image-ja.png',
    ogImageAlt: 'Travo — 毎日は、あっという間。Travo が覚えています。iOS・Android 対応の旅の記録、パスポート、旅程プランナー。',
    home: {
      title: 'Travo｜旅行記録・旅のしおりアプリ：行った国の地図とパスポート',
      description:
        'Travo は無料の旅行記録アプリ。家を出ると旅を自動で記録し、行った都市や国を地図に残します。新しい国に行くたびにパスポートにスタンプが増え、友達と日ごとの旅のしおり（旅程）も作れます。iOS・Android 対応。',
      keywords:
        '旅行記録アプリ, 旅の記録, 足あと地図, 行った国, 旅行パスポート, 旅行計画, 旅のしおり アプリ, 旅程アプリ, 旅行日記, チェックイン, Travo'
    },
    about: {
      title: 'Travo について｜毎日の物語を、残しておくために',
      description:
        '毎日はあっという間に過ぎていくけれど、大切な瞬間は忘れたくない。そんな思いから Travo は生まれました。旅を自動で記録し、あなたが選んだ瞬間を残し、次の旅の計画もお手伝いします。',
      keywords: 'Travo について, Travo Labs, 旅の自動記録, 旅行パスポート, 旅行計画, 旅行日記, プライバシー'
    }
  },

  hero: {
    eyebrow: '旅は、自動で始まる',
    titleLead: '毎日は、あっという間。',
    titleAccent: 'Travo が覚えています。',
    body: '家を出れば、Travo が旅の記録を始めます。訪れた都市や国を残し、パスポートにスタンプを押し、次の旅のプランもまとめておけます。あなたは出かけるだけ。',
    photoAlt: '朝焼けの谷の上に浮かぶ熱気球',
    phoneAlt: 'Travo のホーム地図：台北の写真付きチェックインのピンと、旅・F1 の地図モード',
    stampLabel: 'エアメール'
  },

  band: ['地図', '旅', 'チェックイン', '場所', '友達', 'パスポート', '旅程', '発見'],

  features: {
    title: '8 つのストップ、\nひとつの旅の相棒。',
    body: '行った場所を覚えておき、大切な瞬間を残し、次の旅を計画する。Travo との旅は、この 8 つのストップから始まります。',
    items: [
      {
        tag: '01 · 地図',
        title: '旅のすべてを、ひとつの地図に',
        body: '旅、保存した場所、チェックイン、友達。すべてがひとつの地図にまとまります。レイヤーを切り替えて、出かけるほど埋まっていく世界を眺めましょう。'
      },
      {
        tag: '02 · 旅',
        title: '出かけるだけ。Travo が覚えています',
        body: '家を出ると、旅が自動で始まります。訪れた街、都市、国を Travo が記録。入力は必要ありません。'
      },
      {
        tag: '03 · チェックイン',
        title: 'この瞬間を残そう',
        body: 'チェックインは 1 日 3 回まで。写真を添えて、その瞬間と場所を残しましょう。'
      },
      {
        tag: '04 · 場所',
        title: '行きたい場所を保存',
        body: '次の旅で行きたい場所をリストにまとめましょう。Google マップの保存リストも、共有するだけで Travo に取り込めます。'
      },
      {
        tag: '05 · 友達',
        title: '旅をシェアしよう',
        body: '友達の最新のチェックインをチェック。QR コードを読み取るだけで、スタンプカードを交換できます。'
      },
      {
        tag: '06 · パスポート',
        title: '思い出が詰まったパスポート',
        body: '訪れた国ごとにスタンプが集まります。フライトを記録して、どれだけ遠くまで飛んだかを確かめましょう。'
      },
      {
        tag: '07 · 旅程',
        title: '一緒にプランを立てよう',
        body: '保存した場所、宿泊、フライトで日ごとの旅程を組み立てられます。友達を招待して、一緒に計画しましょう。'
      },
      {
        tag: '08 · 発見',
        title: '次の冒険を見つけよう',
        body: 'ほかの旅行者が公開した旅程を探してみましょう。自分の旅に追加して、あなた好みにアレンジできます。'
      }
    ],
    extrasLabel: 'ほかにもこんな機能',
    extras: [
      'ホーム画面ウィジェット：パスポート、次の旅、旅程',
      'シェアできる毎月の振り返り',
      'F1 プラグイン：レース日程、結果、ランキングを地図に表示'
    ]
  },

  checkins: {
    eyebrow: '03 · チェックイン',
    title: '今日にスタンプを。\n1 日 3 つまで。',
    body: '毎日の足あとは Travo が自動で記録します。チェックインは、あなたが選ぶ瞬間。写真を添えれば、立っていた場所を地図が覚えておきます。1 日にいくつか残すだけで、あなただけの旅日記に。',
    stamps: ['最高のごはん', '絶景スポット', '友達と遊んだ日'],
    screenshotAlt: '2026 年 6 月のデイリーハイライトのカレンダー。写真付きのチェックインとアイスランドの旅'
  },

  passport: {
    eyebrow: '06 · パスポート',
    title: '訪れた国ごとに、\nスタンプをひとつ。',
    body: '旅をするほど、パスポートが自然と埋まっていきます。1 年ごとにスタンプのページができ、訪れた国の数と旅に出ていた日数も自動で数えてくれます。',
    points: [
      '1 年ごとのスタンプページ。国の数と旅の日数を自動で集計',
      'フライトをパスポートに記録して、飛んだ距離（km）を表示',
      '写真と訪れた場所をまとめた毎月の振り返りを、すぐにシェア',
      'ホーム画面に置けるパスポートウィジェット'
    ],
    sample: 'サンプル',
    thailand: 'タイ',
    indonesia: 'インドネシア',
    japanAlt: 'サンプルのスタンプ：日本・京都',
    taiwanAlt: 'サンプルのスタンプ：台湾・台北'
  },

  plan: {
    eyebrow: '07 & 08 · 旅程 + 発見',
    title: '毎日のプランを立てる。\n誰かのプランから始める。',
    body: '保存した場所を日ごとの旅程に。予定、宿泊、フライトをひとつにまとめられます。共有すれば友達も編集できます。または「発見」で誰かが立てた旅程を見つけて自分の旅に追加し、あなた流にアレンジしましょう。',
    points: [
      '予定、宿泊、フライトを日ごとのタイムラインに',
      'まだ予約していないものがひと目でわかる予約リスト',
      'いまの予定と次の予定がわかるホーム画面ウィジェット',
      '発見：国や日数でプランを探して、自分の旅に追加'
    ],
    widget: {
      day: '2/10 日目 · ゴールデンサークル',
      label: 'ウィジェット',
      nowLabel: '現在 · 09:30',
      now: 'シンクヴェトリル国立公園',
      nextLabel: '次 · 12:30',
      next: 'ゲイシール & ストロックル',
      later: '今日このあと：グトルフォスの滝',
      more: 'ほか 1 件'
    },
    discover: {
      kicker: '発見 · アイスランド',
      length: '10 日間',
      title: 'アイスランド 10 日間ドライブ旅行',
      byline: 'Travo（@travo）· 999 人が追加',
      cta: '自分の旅に追加'
    }
  },

  how: {
    eyebrow: '旅のルート',
    title: '設定は一度だけ。あとは出かけるだけ。',
    steps: [
      {
        tag: 'ホームタウン',
        title: 'ホームタウンを設定',
        body: 'これで Travo は、あなたが旅に出ているのか、家に戻ったのかがわかります。'
      },
      {
        tag: '出発',
        title: '位置情報を一度だけ許可',
        body: 'バッテリーにやさしいバックグラウンド記録。設定からいつでもオフにできます。'
      },
      {
        tag: '移動中',
        title: '家を出る',
        body: '旅が自動で始まり、訪れた都市や国が次々と記録されます。'
      },
      {
        tag: '到着',
        title: 'スタンプ、プラン、シェア',
        body: '最高の瞬間にチェックインし、パスポートを埋めて、友達と次の旅を計画しましょう。'
      }
    ],
    note: '以前の旅も残したい？Travo は写真ライブラリから過去の旅を検出できます。読み取るのは写真の撮影場所だけ。写真がアップロードや保存されることはありません。'
  },

  friends: {
    eyebrow: '05 · 友達',
    title: '離れていても、\n一緒に旅しよう。',
    body: '友達を追加すると、最後にチェックインした場所が地図に表示されます。QR コードでスタンプカードを交換したり、プロフィールのリンクを送ったり。読み取って、旅のルートをたどりましょう。',
    to: '宛先：一緒に旅したすべての人へ',
    from: '差出人：app.travolabs.com/@travo',
    cta: 'デモ動画を見る',
    caption: 'あなたが訪れたすべての場所から',
    mapAlt: 'パスポート、カメラ、旅の記念品が置かれた世界地図'
  },

  cta: {
    eyebrow: 'エアメール · ファーストクラス',
    title: 'あなたのパスポートが待っています。',
    body: 'Travo は無料でダウンロードできます。毎日の暮らしも、週末の小旅行も、次に計画している旅も、すべてひとつの場所に。'
  },

  footer: {
    tagline: 'すべての旅を覚えておく。次の旅を計画する。',
    product: 'プロダクト',
    community: 'コミュニティ',
    legal: '規約',
    privacy: 'プライバシーポリシー',
    terms: '利用規約',
    rights: 'TRAVO LABS. ALL RIGHTS RESERVED.'
  },

  about: {
    eyebrow: 'Travo Labs について',
    titleLead: '毎日は、あっという間。',
    titleAccent: 'Travo が覚えています。',
    intro: [
      'Travo は、あなたの人生をかたちづくる場所や瞬間を残しておくために生まれました。見慣れた街角から遠くの都市まで、Travo がそっと足あとを記録するので、あなたは目の前の旅に集中できます。',
      '旅は自動で記録が始まります。訪れた国ごとに、旅のパスポートにスタンプがひとつ。保存した場所は、次の冒険の始まりになります。',
      'Travo を開けば、行った場所を振り返り、友達の旅をのぞき、次の行き先を計画できます。'
    ],
    photoAlt: '夜の街のスカイライン',
    caption: '旅の途中から、こんにちは',
    captionTag: 'NO. 00 · ABOUT',
    stampLabel: 'エアメール',
    missionEyebrow: '私たちのミッション',
    missionLead: '人生という旅の記録を、',
    missionAccent: '手間なく',
    missionTail: '、意味のあるものに。',
    salutation: '旅をするあなたへ',
    letter: [
      '最高の旅アプリとは、使っていることを忘れてしまうアプリだと私たちは考えています。Travo はあなたが家を出たことに気づいて旅の記録を始め、近所の通りから地球の裏側まで、訪れた街、都市、国を残していきます。',
      'それ以外は、あなたが選ぶもの。大切な瞬間にいくつかのチェックイン、スタンプで埋まっていくパスポート、友達と立てたプランや、ほかの旅行者から借りたプラン。あなたは旅を楽しみ、記録は Travo に任せてください。何年たっても、大きな冒険も小さな冒険も、それが起きた場所にちゃんと残っています。'
    ],
    signature: '— Travo',
    whyTitle: 'Travo が選ばれる理由',
    whyBody: '4 つの約束を、スタンプで封をして。',
    reasons: [
      {
        title: '旅は、自動で始まる',
        body: 'ホームタウンを離れると、旅が自動で始まります。訪れた都市や国が記録され、パスポートにスタンプが集まります。'
      },
      {
        title: '残す瞬間は、あなたが選ぶ',
        body: '1 日 3 回までのチェックイン。立っていた場所に写真がピン留めされ、何度も読み返したくなる旅日記になります。'
      },
      {
        title: '一緒に計画できる',
        body: '場所を保存し、友達も編集できる日ごとの旅程を作成。ほかの旅行者が公開したプランから始めることもできます。'
      },
      {
        title: 'プライバシーを第一に',
        body: '記録はいつでもオフにできます。過去の旅の検出で読み取るのは写真の撮影場所だけ。写真がスマートフォンの外に出ることはありません。'
      }
    ],
    ctaEyebrow: '次の消印へ',
    ctaTitle: '思いきり旅を。\n記録は私たちが。',
    ctaBody: 'Travo は iOS と Android で無料。一度許可するだけで、あなたの地図が自然と埋まっていきます。',
    breadcrumbAbout: '私たちについて'
  },

  schema: {
    appDescription:
      'ハンズフリーで使える旅の記録、パスポート、旅程づくりのアプリ。ホームタウンを離れると旅が自動で始まり、訪れた都市と国が記録され、新しい国ごとにパスポートのスタンプが増えます。1 日 3 回までのチェックイン、友達との日ごとの旅程づくり、「発見」で公開されたプランからのスタートにも対応。',
    featureList: [
      'ホームタウンを離れると自動で始まる旅',
      'チェックイン：1 日 3 回まで写真で瞬間を残せる',
      '国ごとのスタンプとフライト記録がある旅のパスポート',
      '宿泊、フライト、共同編集に対応した日ごとの旅程',
      '発見：公開された旅のプランを探して自分の旅に追加',
      'Google マップのリストを取り込める場所リスト',
      '友達マップと QR スタンプカード',
      '写真の撮影場所から過去の旅を検出',
      'ホーム画面ウィジェットと毎月の振り返り'
    ],
    videoName: 'Travo の使い方 — 旅の自動記録アプリのデモ',
    videoDescription:
      '毎日の移動も大きな冒険も Travo が自動で記録し、これまで訪れたすべての場所をインタラクティブな地図にする様子をご覧ください。'
  },

  notFound: {
    title: 'ページが見つかりません｜Travo',
    eyebrow: '差出人に返送',
    heading: 'このあて先は存在しません。',
    body: 'お探しのページは移動した可能性があります。ホームに戻って、そこからもう一度ルートをたどってください。',
    cta: 'ホームに戻る'
  },

  faqSection: {
    eyebrow: 'よくある質問',
    title: 'よくある質問にお答えします。',
    body: 'Travo でできること、料金、そしてあなたの旅を誰が見られるのかをまとめました。'
  },

  faq: [
    {
      question: 'Travo とは何ですか？',
      answer:
        'Travo は Travo Labs が開発した、iOS・Android 対応の無料の旅行記録アプリです。ホームタウンを離れると旅を自動で記録し、行った都市と国を残し、新しい国に行くたびにパスポートにスタンプを押します。友達と日ごとの旅程（旅のしおり）を作ることもできます。'
    },
    {
      question: 'Travo は日本語に対応していますか？',
      answer:
        'はい。Travo のアプリとこのサイトは日本語に対応しています。英語、繁体字中国語、韓国語、タイ語でも利用できます。'
    },
    {
      question: 'Travo はどうやって旅を記録するのですか？',
      answer:
        'バッテリーにやさしいバックグラウンドの位置情報を使います。ホームタウンを設定すると、そこを離れたときに旅が自動で始まり、通り過ぎた街、都市、国がすべて記録されます。入力は必要ありません。'
    },
    {
      question: 'Travo は無料ですか？',
      answer: 'はい。Travo は iOS と Android で無料でダウンロード・利用できます。'
    },
    {
      question: 'バッテリーを消耗しませんか？',
      answer: 'いいえ。バックグラウンドの記録はバッテリー消費を抑えるよう最適化されており、設定からいつでもオフにできます。'
    },
    {
      question: 'チェックインとは何ですか？',
      answer:
        '残しておきたい瞬間のために、1 日 3 回までチェックインできます。写真を添えると立っていた場所を地図が覚えておき、積み重ねるとあなたの旅日記になります。'
    },
    {
      question: 'Travo のパスポートとは何ですか？',
      answer:
        '訪れた国ごとに、パスポートにスタンプがひとつ増えます。1 年ごとにページがあり、訪れた国の数と旅の日数を自動で集計。記録したフライトは飛んだ距離（km）として積み上がります。'
    },
    {
      question: 'Travo で旅の計画はできますか？',
      answer:
        'はい。保存した場所を宿泊やフライトと合わせて日ごとの旅程にでき、共有すれば友達も編集できます。「発見」で公開されたプランを自分の旅に追加することもできます。'
    },
    {
      question: 'Travo を入れる前の旅も追加できますか？',
      answer: 'はい。過去の旅の検出機能が写真の撮影場所を読み取り、以前の旅を提案します。写真がアップロードや保存されることはありません。'
    },
    {
      question: '私の旅は誰が見られますか？',
      answer:
        'あなたが決められます。位置情報は非公開で、フォローできる友達はあなたが選べます。記録は設定からいつでもオフにできます。'
    }
  ]
};
